import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { FFIBrandedV1Report } from "src/pages/reports/ffi/v1/types";
import { isNotNil } from "src/utils/type_guards";
import { DomainsCircle } from "../../graphics/domains_circle";

import { meta } from "./meta";

/*
 * Types
 */

export type NurtureProps = {
    readonly report: FFIBrandedV1Report;
};

/*
 * Main
 */

export const Nurture = (props: NurtureProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="nurture" inline />
                <PageTitle style={{ color: "#99b84e" }}>Nurture</PageTitle>
                <Body>
                    Faith extends to the heart. Belief in Christ is evidence of true heart change (Romans 10:9-10) and
                    the product of divine heart surgery (Ezekiel 36:26-27). True believers do not simply assent to
                    certain truths asserted by the Bible; their desires change to seek after the things of God
                    (Galatians 2:20; 1 John 3:19-24).
                </Body>
                <Body>
                    A Christian school works closely with the local church and with parents to nurture a certain heart
                    disposition towards the Christian faith. For many schools, nurturing spiritual formation emerges
                    from a partnership with church and family and yields close relationships between students, leaders,
                    and families (Deuteronomy 6; Joshua 24; Psalms 78, 127). At the same time, it is important to note
                    that covenantal and missional schools may interpret some of this data differently, while still
                    providing important insights to both types of schools into the nature of the school’s relationships
                    with its families and parents. FFI data analysis identified constructs related to the Nurture domain
                    for six of the seven constituent groups (students; parents; alumni; teachers;
                    leaders/administrators; and support staff), below.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FFI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.nurture),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle style={{ color: "#99b84e" }}>{m.title}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        backgroundColor="#99b94e"
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.nurture[construct] * 100,
                                            },
                                            {
                                                label: "FFI Benchmark",
                                                value: report.value.results.benchmarks.nurture[construct] * 100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
